import {
  Habit,
  RepeatingTask,
  Task,
  TaskFilterOptions,
  TaskType,
  Timestamp,
  WeekDays,
} from '@griply/types';

import { filterHabits } from './filter-habits';
import { filterRepeatingTasks } from './filter-repeating-tasks';
import { filterTasksClientSide } from './filter-tasks-client-side';
import {
  isWithinDeadlineDeadlineWindow,
  isWithinDeadlineWindow,
} from './task-predicates';

// Reproduces the constraints the task query enforces and client-side filtering never re-applies.
export const matchesTaskQueryConstraints = (
  filters: TaskFilterOptions,
  task: Task,
): boolean => {
  // A type filter without Task drops the plain-task query entirely, matching nothing here.
  if (filters.types?.length && !filters.types.includes(TaskType.Task)) {
    return false;
  }

  if (!filters.showCompleted && task.completedAt) {
    return false;
  }

  if (
    (filters.deadlineDeadlineStartDate !== undefined ||
      filters.deadlineDeadlineEndDate !== undefined) &&
    !isWithinDeadlineDeadlineWindow(task, {
      start: filters.deadlineDeadlineStartDate,
      end: filters.deadlineDeadlineEndDate,
    })
  ) {
    return false;
  }

  if (
    !filters.showCompleted &&
    (filters.deadlineStartDate !== undefined ||
      filters.deadlineEndDate !== undefined) &&
    !isWithinDeadlineWindow(task, {
      start: filters.deadlineStartDate,
      end: filters.deadlineEndDate,
    })
  ) {
    return false;
  }

  return true;
};

export type CountMatchingItemsOptions = {
  filters: TaskFilterOptions[];
  tasks: Task[];
  habits: Habit[];
  repeatingTasks: RepeatingTask[];
  weekStartsOn: WeekDays;
  today: Timestamp;
};

// How many items a filter document selects across all three buckets. Callers
// pass tasks already partitioned to `TaskType.Task`; habits and repeating tasks
// are counted through their own pipelines.
export const countMatchingItems = ({
  filters,
  tasks,
  habits,
  repeatingTasks,
  weekStartsOn,
  today,
}: CountMatchingItemsOptions): number => {
  if (!filters.length) {
    return 0;
  }

  const sourcedTasks = tasks.filter((task) =>
    filters.some((filter) => matchesTaskQueryConstraints(filter, task)),
  );

  return (
    filterTasksClientSide(sourcedTasks, filters).length +
    filterHabits({ habits, filters, weekStartsOn, today }).length +
    filterRepeatingTasks({ repeatingTasks, filters, weekStartsOn, today })
      .length
  );
};
